// TAS-360: every game_events_v2 write goes through the append_game_event RPC.
// The direct insert let anyone holding the anon key forge a gm-next (or any
// other event) into any live game. The RPC proves the caller is a player in
// the session by checking the player id against the claim token this device
// was handed at join time (claimTokenStorage), and additionally checks
// is_gamemaster for the GM-only event types. The two TV events are the
// exception: the TV display has no player, so they go out with no id and no
// token.
//
// The RPC returns the new event id, or null when the row already existed (the
// gm-next dedupe index — the server swallows the unique violation). A null
// `data` with no `error` is therefore success, exactly like the old
// insertIgnoreConflict queue op.

import { supabase } from '@tasted/utils/supabase';
import { claimTokenStorage } from './playerStorage';

export const TV_EVENT_TYPES = new Set(['tv-display-online', 'wine-revealed']);

const isTvEvent = (eventType) => TV_EVENT_TYPES.has(eventType);

/**
 * Build the RPC args for append_game_event. Exported so call sites that
 * already have the token (or want to inspect what will be sent) can reuse it.
 */
export function buildAppendArgs({ sessionCode, eventType, payload, playerId, claimToken }) {
  const args = {
    p_session_id: sessionCode,
    p_event_type: eventType,
    p_payload: payload ?? {},
  };
  if (isTvEvent(eventType)) return args;
  const id = playerId || null;
  const token = claimToken ?? claimTokenStorage.get(id);
  args.p_player_id = id;
  args.p_claim_token = token || null;
  return args;
}

/**
 * Append one game event. Returns `{ data, error }` in the supabase shape so a
 * call site that used to `await supabase.from('game_events_v2').insert(...)`
 * changes only the call. `data` is the new event id, or null when the row was
 * already there (dedupe) — treat null-with-no-error as success.
 */
export async function appendGameEvent({ sessionCode, eventType, payload, playerId, claimToken }) {
  const args = buildAppendArgs({ sessionCode, eventType, payload, playerId, claimToken });
  const res = await supabase.rpc('append_game_event_secure', args);
  return { data: res?.data ?? null, error: res?.error ?? null };
}

/**
 * The request-queue op for the same write ({ rpc, args } — see
 * requestQueue.js defaultPersistExecutor). The token is resolved now, at
 * enqueue time, so a replay after a reload carries the same credentials the
 * player had when they tapped.
 */
export function gameEventOp({ sessionCode, eventType, payload, playerId, claimToken }) {
  return {
    rpc: 'append_game_event_secure',
    args: buildAppendArgs({ sessionCode, eventType, payload, playerId, claimToken }),
  };
}
